import React, {type ReactNode, useContext, useEffect, useState} from "react";
import {useUser} from "@/components/Providers/JWTProvider.tsx";
import useGetFees from "@/hooks/useGetFees.ts";
import useGetPurchaseHistory from "@/hooks/useGetPurchaseHistory.ts";
import useGetStudentFees from "@/hooks/useGetStudentFees.ts";
import {type AssessedFee, DUE, PAID, PARTIAL} from "@/types/AssessedFee.ts";
import type {Fee} from "@/types/Fee.ts";

export type PurchaseHistory = {
    fees: Record<string, number>;
    partialStudentFeeIdsAndAmountRemaining: Record<string, number>;
};
export type FeesState = {
    assessedFees: AssessedFee[];
    purchasedFees: AssessedFee[];
    allAssessedFees: AssessedFee[];
    optionalFees: Fee[];
    refreshFees: () => Promise<void>;
    purchaseHistory: PurchaseHistory;
    refreshPurchaseHistory: () => Promise<void>;
    doGetStudentFees: () => Promise<void>;
};

type Props = {
    children?: ReactNode;
};

export const FeesProviderContext = React.createContext<FeesState>({
    allAssessedFees: [],
    assessedFees: [],
    optionalFees: [],
    purchasedFees: [],
    refreshFees: async () => {},
    purchaseHistory: {
        partialStudentFeeIdsAndAmountRemaining: {},
        fees: {},
    },
    refreshPurchaseHistory: async () => {},
    doGetStudentFees: async () => {},
});

const FeeProvider: React.FC<Props> = ({children}: Props) => {
    const {user} = useUser();

    const [optionalFees, setOptionalFees] = useState<Fee[]>([]);
    const [assessedFees, setAssessedFees] = useState<AssessedFee[]>([]);
    const [purchasedFees, setPurchasedFees] = useState<AssessedFee[]>([]);
    const [allAssessedFees, setAllAssessedFees] = useState<AssessedFee[]>([]);
    const [purchaseHistory, setPurchaseHistory] = useState<PurchaseHistory>({
        partialStudentFeeIdsAndAmountRemaining: {},
        fees: {},
    });

    const getFees = useGetFees();
    const getStudentFees = useGetStudentFees();
    const getPurchaseHistory = useGetPurchaseHistory();

    const refreshFees = async () => {
        await Promise.all([doGetFees(), doGetStudentFees(), refreshPurchaseHistory()]);
    };

    const doGetFees = async () => {
        const response = await getFees();

        if (response && response.ok) {
            const {fees} = (await response.json()) as {fees: Fee[]};
            setOptionalFees(fees);
        }
    };

    const refreshPurchaseHistory = async () => {
        const response = await getPurchaseHistory();

        if (response && response.ok) {
            const purchaseHistory = (await response.json()) as PurchaseHistory;
            setPurchaseHistory(purchaseHistory);
        }
    };

    const doGetStudentFees = async () => {
        const response = await getStudentFees();

        if (response && response.ok) {
            const {studentFees} = (await response.json()) as {studentFees: AssessedFee[]};
            setAssessedFees(
                studentFees.filter(
                    f => f.status === DUE || (f.status === PARTIAL && !f.selfAssessed),
                ),
            );
            setPurchasedFees(studentFees.filter(f => [PAID, PARTIAL].includes(f.status)));
            setAllAssessedFees(studentFees);
        }
    };

    useEffect(() => {
        if (user) {
            refreshFees().catch(e => console.error(e));
        }
    }, [user]);

    const value: FeesState = {
        allAssessedFees,
        optionalFees,
        assessedFees,
        purchasedFees,
        refreshFees,
        purchaseHistory,
        refreshPurchaseHistory,
        doGetStudentFees,
    };

    return <FeesProviderContext.Provider value={value}>{children}</FeesProviderContext.Provider>;
};

export const useFeeProvider = (): FeesState => {
    return useContext(FeesProviderContext);
};

export default FeeProvider;
